import { useState } from 'react';
import { useAppStore } from '@/store/AppStore';
import { formatTL } from '@/lib/format';
import { jobPaid, jobRemaining, computeFinance } from '@/lib/finance';
import PaymentForm from './components/PaymentForm';
import Badge from '@/components/base/Badge';

export default function Tahsilatlar() {
  const { jobs, payments, expenses } = useAppStore();
  const [formOpen, setFormOpen] = useState(false);
  const [prefillJobId, setPrefillJobId] = useState<string>('');
  const [prefillCustomer, setPrefillCustomer] = useState<string>('');

  const finance = computeFinance(jobs, payments, expenses);

  function dayOf(dateStr: string): number {
    const m = dateStr.match(/(\d+)/);
    return m ? parseInt(m[1], 10) : 0;
  }

  const todayTotal = payments.filter((p) => dayOf(p.date) === 26).reduce((s, p) => s + p.amount, 0);
  const weekTotal = payments.filter((p) => dayOf(p.date) >= 22).reduce((s, p) => s + p.amount, 0);

  const completedWithBalance = jobs.filter(
    (j) => j.status === 'Kapandı' && jobRemaining(j, payments) > 0,
  );
  const overdueTotal = completedWithBalance.reduce((s, j) => s + jobRemaining(j, payments), 0);

  const summaryCards = [
    { label: 'Bugün Tahsil Edilen', value: todayTotal, icon: 'ri-wallet-3-line', tone: 'text-emerald-600 bg-emerald-50' },
    { label: 'Bu Hafta', value: weekTotal, icon: 'ri-calendar-event-line', tone: 'text-primary-600 bg-primary-50' },
    { label: 'Bu Ay', value: finance.collected, icon: 'ri-funds-box-line', tone: 'text-primary-600 bg-primary-50' },
    { label: 'Bekleyen Tahsilat', value: finance.openReceivable, icon: 'ri-hourglass-line', tone: 'text-amber-600 bg-amber-50' },
    { label: 'Gecikmiş Tahsilat', value: overdueTotal, icon: 'ri-alert-line', tone: 'text-red-600 bg-red-50' },
  ];

  const jobsWithValue = jobs.filter((j) => j.jobValue > 0);

  function openPayment(jobId: string, customer: string) {
    setPrefillJobId(jobId);
    setPrefillCustomer(customer);
    setFormOpen(true);
  }

  return (
    <div className="mx-auto max-w-[1400px] space-y-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.14em] text-accent-600">
            <span className="h-1.5 w-1.5 rounded-full bg-accent-500" />
            Finans
          </div>
          <h1 className="mt-1 font-heading text-2xl font-bold text-foreground-950 md:text-3xl">Tahsilatlar</h1>
          <p className="mt-1 text-sm text-foreground-500">Kimden ne kadar tahsil edildi, ne kadar kaldı?</p>
        </div>
        <button
          type="button"
          onClick={() => {
            setPrefillJobId('');
            setPrefillCustomer('');
            setFormOpen(true);
          }}
          className="inline-flex items-center gap-2 whitespace-nowrap rounded-lg bg-primary-500 px-4 py-2.5 text-sm font-semibold text-white hover:bg-primary-600"
        >
          <i className="ri-add-line text-base" />
          Ödeme Kaydet
        </button>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-5">
        {summaryCards.map((c) => (
          <div key={c.label} className="rounded-lg border border-background-200 bg-white p-4">
            <div className={`flex h-10 w-10 items-center justify-center rounded-lg ${c.tone}`}>
              <i className={`${c.icon} text-lg`} />
            </div>
            <div className="mt-3 font-heading text-xl font-bold text-foreground-950">
              {formatTL(c.value)}
            </div>
            <div className="mt-0.5 text-xs font-semibold text-foreground-500">{c.label}</div>
          </div>
        ))}
      </div>

      {/* Job financial status */}
      <div className="overflow-hidden rounded-lg border border-background-200 bg-white">
        <div className="flex items-center justify-between border-b border-background-100 px-5 py-4">
          <h2 className="font-heading text-[15px] font-bold text-foreground-950">İş Finansal Durumu</h2>
          <span className="text-xs font-semibold text-foreground-400">{jobsWithValue.length} iş</span>
        </div>
        <ul className="divide-y divide-background-100">
          {jobsWithValue.map((job) => {
            const paid = jobPaid(payments, job.id);
            const remaining = jobRemaining(job, payments);
            const pct = job.jobValue > 0 ? Math.round((paid / job.jobValue) * 100) : 0;
            return (
              <li key={job.id} className="flex flex-col gap-3 px-5 py-4 sm:flex-row sm:items-center">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <span className="truncate text-sm font-semibold text-foreground-900">{job.customerName}</span>
                    <Badge tone={job.status === 'Kapandı' ? 'green' : 'neutral'}>{job.status}</Badge>
                  </div>
                  <div className="mt-0.5 truncate text-[13px] text-foreground-500">
                    {job.service} · {job.id}
                  </div>
                  <div className="mt-2 flex flex-wrap items-center gap-x-5 gap-y-1 text-xs">
                    <span className="text-foreground-500">
                      Hizmet: <span className="font-semibold text-foreground-800">{formatTL(job.jobValue)}</span>
                    </span>
                    <span className="text-foreground-500">
                      Tahsil: <span className="font-semibold text-emerald-600">{formatTL(paid)}</span>
                    </span>
                    <span className="text-foreground-500">
                      Kalan:{' '}
                      <span className={`font-semibold ${remaining > 0 ? 'text-amber-600' : 'text-foreground-500'}`}>
                        {formatTL(remaining)}
                      </span>
                    </span>
                  </div>
                  <div className="mt-2 h-1.5 w-full max-w-sm overflow-hidden rounded-full bg-background-100">
                    <div
                      className="h-full rounded-full bg-emerald-500 transition-all"
                      style={{ width: `${Math.min(100, pct)}%` }}
                    />
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => openPayment(job.id, job.customerName)}
                  className="inline-flex shrink-0 items-center gap-1.5 whitespace-nowrap rounded-md border border-background-200 px-3 py-2 text-[13px] font-semibold text-foreground-700 hover:bg-background-100"
                >
                  <i className="ri-add-line" />
                  Ödeme Kaydet
                </button>
              </li>
            );
          })}
        </ul>
      </div>

      {/* Payment history */}
      <div className="overflow-hidden rounded-lg border border-background-200 bg-white">
        <div className="flex items-center justify-between border-b border-background-100 px-5 py-4">
          <h2 className="font-heading text-[15px] font-bold text-foreground-950">Tahsilat Geçmişi</h2>
          <span className="text-xs font-semibold text-foreground-400">{payments.length} kayıt</span>
        </div>
        <ul className="divide-y divide-background-100">
          {payments.map((p) => (
            <li key={p.id} className="flex items-center gap-4 px-5 py-3.5">
              <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-emerald-50 text-emerald-600">
                <i className="ri-arrow-down-circle-line text-lg" />
              </div>
              <div className="min-w-0 flex-1">
                <div className="truncate text-sm font-semibold text-foreground-900">{p.customerName}</div>
                <div className="truncate text-xs text-foreground-500">
                  {p.description || 'Tahsilat'} · {p.method}
                </div>
              </div>
              <div className="hidden text-xs text-foreground-400 sm:block">{p.date}</div>
              <div className="font-heading text-sm font-bold text-emerald-600">{formatTL(p.amount)}</div>
            </li>
          ))}
        </ul>
      </div>

      <PaymentForm open={formOpen} onClose={() => setFormOpen(false)} prefillCustomer={prefillCustomer} prefillJobId={prefillJobId} />
    </div>
  );
}